import { Link, useParams } from "react-router-dom"
import { formatDistanceToNow } from "date-fns"
import { ArrowLeftIcon, ClockIcon } from "@heroicons/react/24/outline"
import { Layout } from "../components/Layout"
import { PageHeader } from "@/components/PageHeader"
import { Skeleton } from "@/components/Skeleton/Skeleton"
import { Button } from "@/components/Button"
import { APIError } from "@/api"
import { useTeamActivity, type TeamActivityItem } from "@/hooks/useTeamActivity"
import { actionLabel } from "@/lib/teamActivityLabels"
import { parseDbTimestampUtc } from "@/lib/dbTimestamp"

function formatWhen(value: string): { absolute: string; relative: string } {
  const date = parseDbTimestampUtc(value)
  if (Number.isNaN(date.getTime())) {
    return { absolute: value, relative: "" }
  }
  return {
    absolute: date.toLocaleString(),
    relative: formatDistanceToNow(date, { addSuffix: true }),
  }
}

function ActivityRow({
  activity,
  currentTeamName,
}: {
  activity: TeamActivityItem
  currentTeamName: string
}) {
  const { absolute, relative } = formatWhen(activity.created_at)
  const hasDetail = activity.before_state !== null || activity.after_state !== null

  return (
    <div className="border-b border-border/50 px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-primary">{actionLabel(activity.action)}</span>
            <span className="rounded bg-background px-1.5 py-0.5 font-mono text-xs text-secondary">
              {activity.action}
            </span>
            {activity.source === "stripe_webhook" && (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                Stripe webhook
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-secondary">
            {activity.source === "stripe_webhook"
              ? `On behalf of ${activity.actor.name}`
              : activity.actor.name}
            {/* resource_name is a denormalized snapshot from write time — show it
                only when it differs from the team's current name, i.e. the team
                was renamed since this row was written. */}
            {activity.resource_name && activity.resource_name !== currentTeamName && (
              <> · team was "{activity.resource_name}" at the time</>
            )}
          </p>
        </div>
        <div className="text-right text-sm text-secondary" title={absolute}>
          {absolute}
          {relative && <div className="text-xs text-secondary/70">{relative}</div>}
        </div>
      </div>

      {hasDetail && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-secondary hover:text-primary">
            Details
          </summary>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {activity.before_state !== null && (
              <div>
                <p className="mb-1 text-xs font-medium text-secondary">Before</p>
                <pre className="max-h-64 overflow-auto rounded-md bg-background p-2 text-xs text-primary">
                  {JSON.stringify(activity.before_state, null, 2)}
                </pre>
              </div>
            )}
            {activity.after_state !== null && (
              <div>
                <p className="mb-1 text-xs font-medium text-secondary">After</p>
                <pre className="max-h-64 overflow-auto rounded-md bg-background p-2 text-xs text-primary">
                  {JSON.stringify(activity.after_state, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  )
}

export function TeamActivity() {
  const { teamId } = useParams<{ teamId: string }>()
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useTeamActivity(teamId!)

  if (error instanceof APIError && error.status === 404) {
    return (
      <Layout>
        <div className="space-y-6">
          <PageHeader
            title={
              <div className="flex items-center space-x-3">
                <Link to="/teams" className="p-2 hover:bg-surface rounded-md transition-colors">
                  <ArrowLeftIcon className="h-5 w-5" />
                </Link>
                <span>Activity Log</span>
              </div>
            }
          />
          <div className="rounded-lg border border-border bg-surface p-6 text-center text-secondary">
            Team not found.
          </div>
        </div>
      </Layout>
    )
  }

  // Only a failure with nothing loaded takes over the page. `error` is also set
  // when a later page or a background refetch fails, and the rows already on
  // screen are still good then — that case is reported inline below.
  if (error && !data) {
    return (
      <Layout>
        <div className="rounded-lg border border-border bg-surface p-6 text-center text-rose-500">
          Failed to load the activity log.
        </div>
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    )
  }

  const team = data.pages[0].team
  const activities = data.pages.flatMap((page) => page.activities)

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title={
            <div className="flex items-center space-x-3">
              <Link
                to={`/teams/${team.public_id}`}
                className="p-2 hover:bg-surface rounded-md transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </Link>
              <span>Activity Log</span>
              <span className="text-secondary font-normal">· {team.name}</span>
            </div>
          }
        />

        {activities.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface p-6 text-secondary">
            <ClockIcon className="h-5 w-5" />
            No recorded activity for this team yet.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-surface">
            {activities.map((activity) => (
              <ActivityRow key={activity.id} activity={activity} currentTeamName={team.name} />
            ))}
          </div>
        )}

        {error && (
          <p className="text-center text-sm text-rose-500">
            Failed to load the latest activity. What is shown may be incomplete.
          </p>
        )}

        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="secondary"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  )
}
